import { css } from "glamor";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { InfoTooltip } from "./InfoTooltip";
import { PLH_TILE_INFO } from "./plhTileInfo";

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

/* Theme-aware tokens: use CSS variables from .plh-dashboard so the tile respects light/dark theme */
const surface1 = "var(--s1)";
const border = "var(--brd)";
const textPrimaryVar = "var(--t1)";
const textMutedVar = "var(--t3)";

export interface FunnelTrendData {
  weeks: string[];
  weekLabels: string[];
  visits: number[];
  signups: number[];
  setup: number[];
  activated: number[];
  paid: number[];
}

interface FunnelTrendTileProps {
  funnelTrend?: FunnelTrendData | null;
  periodKind?: "week" | "month";
}

const COLORS = {
  visits: "#3d8bfd",
  signups: "#818cf8",
  setup: "#a78bfa",
  activated: "#c084fc",
  paid: "#34d399",
};

export function FunnelTrendTile({
  funnelTrend,
  periodKind = "week",
}: FunnelTrendTileProps) {
  // Resolve CSS variable colors so Chart.js doesn't default to pure black,
  // and so the chart respects light/dark theme.
  const rootStyle =
    typeof window !== "undefined"
      ? getComputedStyle(document.documentElement)
      : null;

  const axisColor =
    rootStyle?.getPropertyValue("--t3")?.trim() || "rgba(148, 163, 184, 1)";
  const primaryTextColor =
    rootStyle?.getPropertyValue("--t1")?.trim() || "rgba(15, 23, 42, 1)";
  const gridColor =
    rootStyle?.getPropertyValue("--brd")?.trim() || "rgba(148, 163, 184, 0.2)";

  const hasData =
    funnelTrend &&
    funnelTrend.weeks &&
    funnelTrend.weeks.length > 0 &&
    funnelTrend.visits.some((v) => v > 0);

  const labels = funnelTrend?.weekLabels?.length
    ? funnelTrend.weekLabels
    : funnelTrend?.weeks?.map((w, idx) => `W${idx + 1}`) ?? [];

  const data = {
    labels,
    datasets: [
      {
        label: "Visits (right axis)",
        data: funnelTrend?.visits ?? [],
        borderColor: COLORS.visits,
        backgroundColor: COLORS.visits,
        borderWidth: 2,
        tension: 0,
        fill: false,
        yAxisID: "y2",
        borderDash: [6, 3],
        pointRadius: 2,
        pointHoverRadius: 3,
      },
      {
        label: "Signups",
        data: funnelTrend?.signups ?? [],
        borderColor: COLORS.signups,
        backgroundColor: COLORS.signups,
        borderWidth: 2,
        tension: 0,
        fill: false,
        yAxisID: "y1",
        pointRadius: 2,
        pointHoverRadius: 3,
      },
      {
        label: "Setup",
        data: funnelTrend?.setup ?? [],
        borderColor: COLORS.setup,
        backgroundColor: COLORS.setup,
        borderWidth: 2,
        tension: 0,
        fill: false,
        yAxisID: "y1",
        pointRadius: 2,
        pointHoverRadius: 3,
      },
      {
        label: "Activated",
        data: funnelTrend?.activated ?? [],
        borderColor: COLORS.activated,
        backgroundColor: COLORS.activated,
        borderWidth: 2,
        tension: 0,
        fill: false,
        yAxisID: "y1",
        pointRadius: 2,
        pointHoverRadius: 3,
      },
      {
        label: "Paid",
        data: funnelTrend?.paid ?? [],
        borderColor: COLORS.paid,
        backgroundColor: COLORS.paid,
        borderWidth: 2,
        tension: 0,
        fill: false,
        yAxisID: "y1",
        pointRadius: 2,
        pointHoverRadius: 3,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          // Use small bar-like swatches instead of big dots to match the mock.
          usePointStyle: false,
          boxWidth: 32,
          boxHeight: 3,
          borderRadius: 999,
          color: axisColor,
          font: {
            size: 10,
          },
        },
      },
      tooltip: {
        callbacks: {
          title(items: unknown[]) {
            const idx = (items as { dataIndex?: number }[])[0]?.dataIndex ?? 0;
            const week = funnelTrend?.weeks?.[idx];
            const label = (items as { label?: string }[])[0]?.label;
            return funnelTrend?.weekLabels?.[idx] && week
              ? `${funnelTrend.weekLabels[idx]} · ${week}`
              : label ?? (periodKind === "month" ? "Month" : "Week");
          },
          label(ctx: { dataset: { label?: string }; parsed: { y?: number | null } }) {
            const val = ctx.parsed.y;
            return `${ctx.dataset.label ?? ""}: ${val != null ? val.toLocaleString() : "—"}`;
          },
        },
        titleColor: primaryTextColor,
        bodyColor: axisColor,
      },
    },
    scales: {
      x: {
        ticks: {
          color: axisColor,
          font: { size: 10 },
        },
        grid: {
          color: gridColor,
          borderDash: [3, 3],
        },
      },
      y1: {
        type: "linear" as const,
        position: "left" as const,
        beginAtZero: true,
        ticks: {
          color: axisColor,
          font: { size: 10 },
        },
        grid: {
          color: gridColor,
          borderDash: [3, 3],
        },
      },
      y2: {
        type: "linear" as const,
        position: "right" as const,
        beginAtZero: true,
        ticks: {
          color: axisColor,
          font: { size: 10 },
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  return (
    <div
      {...css({
        background: surface1,
        borderRadius: 12,
        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.06)",
        border: `1px solid ${border}`,
        padding: 24,
      })}
    >
      <div
        {...css({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
          gap: 12,
          flexWrap: "wrap",
        })}
      >
        <div
          {...css({
            display: "flex",
            alignItems: "center",
            gap: 8,
          })}
        >
          <h2
            {...css({
              fontSize: "0.9rem",
              fontWeight: 700,
              letterSpacing: -0.2,
              margin: 0,
              color: textPrimaryVar,
            })}
          >
            Funnel Trend
          </h2>
          <span
            {...css({
              fontSize: "0.65rem",
              padding: "2px 8px",
              borderRadius: 999,
              border: `1px solid ${border}`,
              color: textMutedVar,
            })}
          >
            Last {labels.length || 0} {periodKind === "month" ? "months" : "weeks"}
          </span>
        </div>
        <InfoTooltip text={PLH_TILE_INFO.funnelTrend} />
      </div>

      <div
        {...css({
          fontSize: "0.75rem",
          color: textMutedVar,
          marginBottom: 8,
        })}
      >
        Weekly Performance
      </div>

      <div
        {...css({
          position: "relative",
          height: 220,
        })}
      >
        {hasData ? (
          <Line data={data} options={options} />
        ) : (
          <div
            {...css({
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.8rem",
              color: textMutedVar,
            })}
          >
            No trend data for the selected week yet.
          </div>
        )}
      </div>
    </div>
  );
}

